import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Checkbox, notification, Spin } from 'antd';
import { contracts } from '../api';
import { userRole, employeeId } from '../../GetRole.utils';
import StepDrawer from '../other/StepDrawer';
import {
  Field,
  EntityChoice,
  ClientSelect,
  ProjectSelect,
  TermFields,
  FileDrop,
  AmountInput,
  Summary,
  entityName,
  apiErrorMessage,
  parseApiDate,
  formatDate,
  splitAmount,
  formatAmount,
} from '../other/contractFields';

const STATUS = { draft: 1, pending: 2 };
const PO_NUMBER_REGEX = /^[a-zA-Z0-9\-#/]{1,15}$/;

const { TextArea } = Input;

const capitalizeWords = (value) =>
  value
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// the server validator rejects null / empty strings for typed fields, so leave them out
const withoutEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== ''));

const emptyValues = (prefill) => ({
  seId: prefill?.seId ?? 1,
  clientId: prefill?.clientId ?? null,
  clientLabel: prefill?.clientName ?? prefill?.legalEntityName ?? '',
  projectId: prefill?.projectId ? Number(prefill.projectId) : null,
  projectLabel: prefill?.projectName ?? '',
  linkSow: true,
  sowNumber: prefill?.sowNumber ?? null,
  poNumber: '',
  vendorNo: '',
  currency: '₹',
  amount: '',
  shipTo: '',
  billTo: '',
  sameAddress: false,
  start: null,
  end: null,
  endless: false,
});

// Create or edit a purchase order in a step drawer.
// contractId + status: edit. prefill: lock client/project (and SOW) from the page it was opened on.
const PoDrawer = ({ open, onClose, contractId, status, prefill }) => {
  const isEdit = !!contractId;
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(emptyValues(prefill));
  const [file, setFile] = useState(null);
  const [existingFileName, setExistingFileName] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sowOptions, setSowOptions] = useState([]);

  const canSendForApproval = userRole !== 'finance' && (!isEdit || status === 'draft');
  // editing a draft: only the contracting entity is disabled, every other field stays enabled
  const isDraftEdit = isEdit && status === 'draft';
  const entityLocked = isEdit || !!prefill?.clientId;
  const clientLocked = isDraftEdit ? false : isEdit || !!prefill?.clientId;
  const projectLocked = isDraftEdit ? false : isEdit || !!prefill?.projectId;
  const sowLocked = isDraftEdit ? false : !!prefill?.sowNumber;

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setErrors({});
    setFile(null);
    setExistingFileName('');
    setValues(emptyValues(prefill));
    if (!isEdit) return;

    setLoading(true);
    contracts
      .getContractsData(contractId)
      .then((res) => {
        const data = res.data[0]?.contractData || {};
        const { currency, amount } = splitAmount(data.totalAmount);
        setValues({
          ...emptyValues(),
          seId: Number(data.seId),
          clientId: data.client?.clientId ?? null,
          clientLabel: data.client?.legalEntityName ?? '',
          projectId: data.project?.projectId ?? null,
          projectLabel: data.project?.projectName ?? '',
          linkSow: !!data.sowNumber,
          sowNumber: data.sowNumber || null,
          poNumber: data.poNumber || '',
          vendorNo: data.spanideaVendorNo || '',
          currency,
          amount,
          shipTo: data.shipTo || '',
          billTo: data.billTo || '',
          sameAddress: !!data.shipTo && data.shipTo === data.billTo,
          start: parseApiDate(data.activationDate),
          end: parseApiDate(data.expiryDate),
          endless: data.expiryDate == null,
        });
        setExistingFileName(data.fileName || '');
      })
      .catch((err) => console.error('Error fetching PO:', err))
      .finally(() => setLoading(false));
    // prefill is read once when the drawer opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId]);

  useEffect(() => {
    if (!open || !values.projectId) {
      setSowOptions([]);
      return;
    }
    contracts
      .getSowDropDown(values.projectId)
      .then((res) =>
        setSowOptions((res.data || []).map((s) => ({ value: s.sowNumber, label: s.sowNumber })))
      )
      .catch((err) => console.error('Error fetching SOWs:', err));
  }, [open, values.projectId]);

  const update = (patch) => {
    setValues((prev) => {
      const next = { ...prev, ...patch };
      if (next.sameAddress) next.billTo = next.shipTo;
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      if (patch.end !== undefined || patch.endless !== undefined) delete next.end;
      if (patch.linkSow !== undefined) delete next.sowNumber;
      if (patch.sameAddress || patch.shipTo !== undefined) delete next.billTo;
      return next;
    });
  };

  const stepErrors = (index) => {
    const e = {};
    if (index === 0) {
      if (!values.clientId) e.clientId = 'Choose a client';
      if (!values.projectId) e.projectId = 'Choose a project';
      if (values.linkSow && !values.sowNumber) e.sowNumber = 'Choose the SOW, or untick "Linked to a SOW"';
      if (!values.poNumber) e.poNumber = 'Enter the PO number';
      else if (!PO_NUMBER_REGEX.test(values.poNumber))
        e.poNumber = 'Letters, numbers and - # / only, up to 15 characters';
      if (!values.vendorNo) e.vendorNo = 'Enter the Spanidea vendor number';
    }
    if (index === 1) {
      if (!values.amount) e.amount = 'Enter the PO amount';
      if (!values.shipTo.trim()) e.shipTo = 'Enter the shipping address';
      if (!values.billTo.trim()) e.billTo = 'Enter the billing address';
      if (!values.start) e.start = 'Choose a start date';
      if (!values.endless && !values.end) e.end = 'Choose an end date, or tick "No end date"';
    }
    if (index === 2 && !file && !existingFileName) e.file = 'Upload the PO document';
    return e;
  };

  const goNext = () => {
    const e = stepErrors(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(step + 1);
  };

  const save = (cStatusId) => {
    if (cStatusId === STATUS.pending) {
      for (let i = 0; i < 3; i++) {
        const e = stepErrors(i);
        if (Object.keys(e).length) {
          setErrors(e);
          setStep(i);
          return;
        }
      }
    }

    const obj = withoutEmpty({
      cStatusId,
      seId: Number(values.seId),
      clientId: values.clientId,
      projectId: values.projectId,
      poNumber: values.poNumber,
      spanideaVendorNo: String(values.vendorNo),
      contractTypeId: 3,
      shipTo: values.shipTo,
      billTo: values.billTo,
      totalAmount: values.amount ? values.currency + values.amount : '',
      activationDate: values.start,
      expiryDate: !values.endless ? values.end : null,
      sowNumber: values.linkSow && values.sowNumber ? String(values.sowNumber) : null,
      createdBy: employeeId,
    });

    const formdata = new FormData();
    if (file) formdata.append('file', file);
    formdata.append('obj', JSON.stringify(obj));

    setSaving(cStatusId === STATUS.pending ? 'approval' : 'draft');
    const request = isEdit
      ? contracts.updateContractsData(contractId, formdata)
      : contracts.addContractsData(formdata);

    request
      .then((res) => {
        notification.success({
          message: cStatusId === STATUS.pending ? 'PO sent for approval' : res?.message || 'PO saved',
        });
        onClose(true);
      })
      .catch((err) => {
        notification.error({
          message: apiErrorMessage(err, 'Could not save the PO'),
        });
      })
      .finally(() => setSaving(null));
  };

  const steps = [
    {
      key: 'basics',
      title: 'Basics',
      content: loading ? (
        <Spin />
      ) : (
        <div className="sd-form">
          <EntityChoice
            value={values.seId}
            onChange={(seId) =>
              update({ seId, clientId: null, clientLabel: '', projectId: null, projectLabel: '', sowNumber: null })
            }
            disabled={entityLocked}
          />
          <ClientSelect
            seId={values.seId}
            value={values.clientId}
            onChange={(clientId, option) =>
              update({ clientId, clientLabel: option?.label || '', projectId: null, projectLabel: '', sowNumber: null })
            }
            disabled={clientLocked}
            currentOption={values.clientId ? { value: values.clientId, label: values.clientLabel } : null}
            error={errors.clientId}
          />
          <ProjectSelect
            clientId={values.clientId}
            value={values.projectId}
            onChange={(projectId, option) =>
              update({ projectId, projectLabel: option?.label || '', sowNumber: null })
            }
            disabled={projectLocked}
            currentOption={values.projectId ? { value: values.projectId, label: values.projectLabel } : null}
            error={errors.projectId}
          />
          <Field
            label="SOW"
            required={values.linkSow}
            error={errors.sowNumber}
            extra={
              !sowLocked && (
                <Checkbox
                  checked={values.linkSow}
                  onChange={(e) => update({ linkSow: e.target.checked, sowNumber: e.target.checked ? values.sowNumber : null })}
                >
                  Linked to a SOW
                </Checkbox>
              )
            }
          >
            <Select
              size="large"
              placeholder={values.projectId ? 'Choose the SOW this PO covers' : 'Choose a project first'}
              value={values.sowNumber}
              onChange={(sowNumber) => update({ sowNumber, linkSow: true })}
              options={
                values.sowNumber && !sowOptions.some((o) => o.value === values.sowNumber)
                  ? [{ value: values.sowNumber, label: values.sowNumber }, ...sowOptions]
                  : sowOptions
              }
              disabled={sowLocked || !values.linkSow || !values.projectId}
              status={errors.sowNumber ? 'error' : ''}
              notFoundContent="No SOWs for this project"
            />
          </Field>
          <div className="sd-row">
            <Field label="PO number" required error={errors.poNumber}>
              <Input
                size="large"
                maxLength={15}
                value={values.poNumber}
                status={errors.poNumber ? 'error' : ''}
                placeholder="e.g. 4500018822"
                onChange={(e) => update({ poNumber: e.target.value.replace(/[^a-zA-Z0-9-#/]/g, '') })}
              />
            </Field>
            <Field label="Spanidea vendor no." required error={errors.vendorNo}>
              <Input
                size="large"
                maxLength={10}
                value={values.vendorNo}
                status={errors.vendorNo ? 'error' : ''}
                placeholder="As registered with the client"
                onChange={(e) => update({ vendorNo: e.target.value.replace(/[^\w\s]/gi, '') })}
              />
            </Field>
          </div>
        </div>
      ),
    },
    {
      key: 'commercials',
      title: 'Commercials',
      content: (
        <div className="sd-form">
          <AmountInput
            label="PO amount"
            currency={values.currency}
            amount={values.amount}
            onChange={update}
            error={errors.amount}
          />
          <Field label="Shipping address" required error={errors.shipTo}>
            <TextArea
              autoSize={{ minRows: 2, maxRows: 5 }}
              value={values.shipTo}
              status={errors.shipTo ? 'error' : ''}
              placeholder="Street, city, state, PIN"
              onChange={(e) => update({ shipTo: capitalizeWords(e.target.value) })}
            />
          </Field>
          <Field
            label="Billing address"
            required
            error={errors.billTo}
            extra={
              <Checkbox
                checked={values.sameAddress}
                onChange={(e) => update({ sameAddress: e.target.checked })}
              >
                Same as shipping
              </Checkbox>
            }
          >
            <TextArea
              autoSize={{ minRows: 2, maxRows: 5 }}
              value={values.billTo}
              disabled={values.sameAddress}
              status={errors.billTo ? 'error' : ''}
              placeholder="Street, city, state, PIN"
              onChange={(e) => update({ billTo: capitalizeWords(e.target.value) })}
            />
          </Field>
          <TermFields
            start={values.start}
            end={values.end}
            endless={values.endless}
            onChange={update}
            errors={errors}
          />
        </div>
      ),
    },
    {
      key: 'document',
      title: 'Document',
      content: (
        <div className="sd-form">
          <FileDrop
            file={file}
            existingName={existingFileName}
            onChange={(picked) => {
              setFile(picked);
              setErrors((prev) => ({ ...prev, file: undefined }));
            }}
            error={errors.file}
            label="PO document"
          />
        </div>
      ),
    },
    {
      key: 'review',
      title: 'Review',
      content: (
        <div className="sd-form">
          <Summary
            rows={[
              { label: 'Contracting entity', value: entityName(values.seId) },
              { label: 'Client', value: values.clientLabel },
              { label: 'Project', value: values.projectLabel },
              { label: 'SOW', value: values.linkSow ? values.sowNumber : 'Not linked to a SOW' },
              { label: 'PO number', value: values.poNumber },
              { label: 'Vendor no.', value: values.vendorNo },
              { label: 'PO amount', value: formatAmount(values.currency, values.amount) },
              { label: 'Ship to', value: values.shipTo },
              { label: 'Bill to', value: values.billTo },
              {
                label: 'Term',
                value: values.start
                  ? `${formatDate(values.start)} → ${values.endless ? 'no end date' : formatDate(values.end)}`
                  : '',
              },
              { label: 'Document', value: file?.name || existingFileName },
            ]}
          />
          {canSendForApproval && (
            <span className="sd-help">
              Sending for approval moves the PO to Pending approval. You can also keep it as a draft.
            </span>
          )}
        </div>
      ),
    },
  ];

  const draftLabel = isEdit ? 'Save changes' : 'Save as draft';

  return (
    <StepDrawer
      open={open}
      title={isEdit ? 'Edit purchase order' : 'New purchase order'}
      subtitle={[values.clientLabel, values.projectLabel].filter(Boolean).join(' · ') || 'Purchase order'}
      steps={steps}
      current={step}
      onStepClick={(index) => (index < step ? setStep(index) : goNext())}
      onBack={() => setStep(step - 1)}
      onNext={goNext}
      onClose={() => onClose(false)}
      footerStart={
        canSendForApproval && (
          <Button onClick={() => save(STATUS.draft)} loading={saving === 'draft'} disabled={!!saving}>
            {draftLabel}
          </Button>
        )
      }
      submitButton={
        canSendForApproval ? (
          <Button
            type="primary"
            onClick={() => save(STATUS.pending)}
            loading={saving === 'approval'}
            disabled={!!saving}
          >
            Send for approval
          </Button>
        ) : (
          <Button type="primary" onClick={() => save(STATUS.draft)} loading={saving === 'draft'} disabled={!!saving}>
            {draftLabel}
          </Button>
        )
      }
    />
  );
};

export default PoDrawer;
